import {
  applicationIdForTool,
  TRIBUNALEA_APPLICATIONS,
  TRIBUNALEA_APPLICATION_IDS,
  TRIBUNALEA_TOOLS,
  TRIBUNALEA_TOOL_IDS,
} from "@tribunalea/shared";
import { Check, SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

function effectiveIds(selection, allIds) {
  return selection?.mode === "selected"
    ? selection.allowedIds || []
    : [...allIds];
}

function createDraft(policy) {
  const applicationIds = effectiveIds(
    policy?.applications,
    TRIBUNALEA_APPLICATION_IDS,
  );
  return {
    applicationIds,
    toolIds: effectiveIds(policy?.tools, TRIBUNALEA_TOOL_IDS)
      .filter((toolId) => applicationIds.includes(applicationIdForTool(toolId))),
  };
}

function selection(ids, allIds) {
  return allIds.every((id) => ids.includes(id))
    ? { mode: "all", allowedIds: [] }
    : { mode: "selected", allowedIds: ids };
}

export function OrganizationAccessPanel({ api, workspace, onUpdated }) {
  const [draft, setDraft] = useState(() => createDraft(workspace.accessPolicy));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(createDraft(workspace.accessPolicy));
  }, [workspace.accessPolicy]);

  const selectedApplications = useMemo(
    () => new Set(draft.applicationIds),
    [draft.applicationIds],
  );

  function toggleApplication(applicationId) {
    setSaved(false);
    setDraft((current) => {
      if (current.applicationIds.includes(applicationId)) {
        return {
          applicationIds: current.applicationIds.filter(
            (id) => id !== applicationId,
          ),
          toolIds: current.toolIds.filter(
            (id) => applicationIdForTool(id) !== applicationId,
          ),
        };
      }
      const applicationToolIds = TRIBUNALEA_TOOLS
        .filter((tool) => tool.applicationId === applicationId)
        .map((tool) => tool.id);
      return {
        applicationIds: [...current.applicationIds, applicationId],
        toolIds: [...new Set([...current.toolIds, ...applicationToolIds])],
      };
    });
  }

  function toggleTool(toolId) {
    setSaved(false);
    setDraft((current) => ({
      ...current,
      toolIds: current.toolIds.includes(toolId)
        ? current.toolIds.filter((id) => id !== toolId)
        : [...current.toolIds, toolId],
    }));
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const updated = await api.updateOrganizationAccessPolicy({
        applications: selection(
          draft.applicationIds,
          TRIBUNALEA_APPLICATION_IDS,
        ),
        tools: selection(draft.toolIds, TRIBUNALEA_TOOL_IDS),
      });
      onUpdated?.(updated);
      setSaved(true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="institution-card organization-access-card">
      <header>
        <SlidersHorizontal size={20} />
        <div>
          <h2>Aplicaciones y herramientas</h2>
          <p>Elegí lo que podrán ver y usar los integrantes de la organización.</p>
        </div>
      </header>
      <form onSubmit={submit}>
        <fieldset>
          <legend>Aplicaciones visibles</legend>
          <div className="institutional-check-grid">
            {TRIBUNALEA_APPLICATIONS.map((application) => (
              <label className="institutional-check" key={application.id}>
                <input
                  type="checkbox"
                  checked={selectedApplications.has(application.id)}
                  onChange={() => toggleApplication(application.id)}
                />
                <span>
                  <Check size={13} />
                  {application.name}
                  {application.comingSoon ? <small>Próximamente</small> : null}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend>Herramientas habilitadas</legend>
          {TRIBUNALEA_APPLICATIONS
            .filter(({ id }) => (
              selectedApplications.has(id)
              && TRIBUNALEA_TOOLS.some((tool) => tool.applicationId === id)
            ))
            .map((application) => (
              <div className="institutional-tool-group" key={application.id}>
                <strong>{application.name}</strong>
                <div className="institutional-check-grid">
                  {TRIBUNALEA_TOOLS
                    .filter((tool) => tool.applicationId === application.id)
                    .map((tool) => (
                      <label className="institutional-check" key={tool.id}>
                        <input
                          type="checkbox"
                          checked={draft.toolIds.includes(tool.id)}
                          onChange={() => toggleTool(tool.id)}
                        />
                        <span><Check size={13} />{tool.name}</span>
                      </label>
                    ))}
                </div>
              </div>
            ))}
          {!draft.applicationIds.some((applicationId) => (
            TRIBUNALEA_TOOLS.some((tool) => tool.applicationId === applicationId)
          )) ? (
            <p className="organization-access-empty">
              Seleccioná una aplicación para configurar sus herramientas.
            </p>
          ) : null}
        </fieldset>

        <div className="organization-access-actions">
          <span>
            {error ? <em className="organization-access-error">{error}</em> : null}
            {saved ? <em>Configuración guardada.</em> : null}
          </span>
          <button type="submit" disabled={saving}>
            {saving ? "Guardando…" : "Guardar accesos"}
          </button>
        </div>
      </form>
    </section>
  );
}
